import {visit} from 'unist-util-visit';
import type {Parent, Root, Text, Image} from 'mdast';
import type {TNode} from 'ghostban';
import {calcMatAndMarkup, SGF_LETTERS, Ki, getRoot} from 'ghostban';
import {getFirstToMoveColorFromRoot} from '../../utils/board';

type StoreWithBoard = {
    getState: () => {
        board: {
            root?: TNode;
            currentNode?: TNode;
        };
    };
};

let getStore: (() => StoreWithBoard) | null = null;

export function setStoreGetter(storeGetter: () => StoreWithBoard) {
    getStore = storeGetter;
}

function generateSgfFromNode(node: TNode, path?: string): string {
    if (!node) {
        return '';
    }

    try {
        const {mat} = calcMatAndMarkup(node);
        const size = mat.length;

        let sgf = `;GM[1]FF[4]SZ[${size}]`;

        const blackStones: string[] = [];
        const whiteStones: string[] = [];

        for (let i = 0; i < size; i++) {
            for (let j = 0; j < size; j++) {
                // matrix transpose
                const stone = mat[j][i];
                if (stone === Ki.Black) {
                    const coord = SGF_LETTERS[j] + SGF_LETTERS[i];
                    blackStones.push(coord);
                } else if (stone === Ki.White) {
                    const coord = SGF_LETTERS[j] + SGF_LETTERS[i];
                    whiteStones.push(coord);
                }
            }
        }

        if (blackStones.length > 0) {
            if (blackStones.length === 1) {
                sgf += `AB[${blackStones[0]}]`;
            } else {
                sgf += `AB[${blackStones.join('][')}]`;
            }
        }

        if (whiteStones.length > 0) {
            if (whiteStones.length === 1) {
                sgf += `AW[${whiteStones[0]}]`;
            } else {
                sgf += `AW[${whiteStones.join('][')}]`;
            }
        }

        if (path) {
            const pathArray = path.match(/.{1,2}/g) || [];
            const rootNode = getRoot(node);
            let turn = rootNode
                ? getFirstToMoveColorFromRoot(rootNode)
                : Ki.Black;
            if (turn === Ki.Empty) {
                turn = Ki.Black;
            }

            pathArray.forEach(moveStr => {
                const i = SGF_LETTERS.indexOf(moveStr[0].toLowerCase());
                const j = SGF_LETTERS.indexOf(moveStr[1].toLowerCase());

                if (i >= 0 && j >= 0) {
                    const coord = SGF_LETTERS[i] + SGF_LETTERS[j];
                    const moveProperty = turn === Ki.Black ? 'B' : 'W';
                    sgf += `;${moveProperty}[${coord}]`;
                    turn = turn === Ki.Black ? Ki.White : Ki.Black;
                }
            });
        }

        return sgf;
    } catch (error) {
        console.warn('Failed to generate SGF from node:', error);
        return '';
    }
}

export function transformRelativeGoBoardThumbnails(): (tree: Root) => void {
    return (tree: Root) => {
        visit(tree, 'text', (node: Text, index, parent) => {
            if (!parent || index === undefined) return;

            const regex = /\[diagram:sgf=(root|here)/g;
            const segments: (Text | Image)[] = [];
            let lastIndex = 0;
            let match;

            while ((match = regex.exec(node.value)) !== null) {
                const start = match.index;
                const relativeType = match[1]; // 'root' or 'here'

                // Find the closing bracket by counting bracket depth
                const afterRelativeType = start + match[0].length;
                let depth = 1; // We're already inside [diagram:sgf=root/here
                let fullEnd = -1;

                for (let i = afterRelativeType; i < node.value.length; i++) {
                    const char = node.value[i];
                    if (char === '[') depth++;
                    else if (char === ']') depth--;
                    if (depth === 0) {
                        fullEnd = i;
                        break;
                    }
                }

                if (fullEnd === -1) continue;

                if (start > lastIndex) {
                    segments.push({
                        type: 'text',
                        value: node.value.slice(lastIndex, start),
                    } as Text);
                }

                const paramString = node.value
                    .slice(afterRelativeType, fullEnd)
                    .trim();

                const params: Record<string, string> = {};
                        paramString.split(/\s+/).forEach((pair: string) => {
                    const eqIndex = pair.indexOf('=');
                    if (eqIndex > 0) {
                        const key = pair.slice(0, eqIndex);
                        const value = pair.slice(eqIndex + 1);
                        params[key] = value;
                    }
                });

                let generatedSgf = '';
                if (getStore) {
                    try {
                        const state = getStore().getState();
                        const boardState = state.board;

                        const pathParam = params.path;

                        if (relativeType === 'root' && boardState.root) {
                            generatedSgf = generateSgfFromNode(
                                boardState.root,
                                pathParam
                            );
                        } else if (
                            relativeType === 'here' &&
                            boardState.currentNode
                        ) {
                            generatedSgf = generateSgfFromNode(
                                boardState.currentNode,
                                pathParam
                            );
                        }
                    } catch (error) {
                        console.warn(
                            'Failed to get board state from store:',
                            error
                        );
                    }
                }

                if (generatedSgf) {
                    const url = new URL(
                        '/api/v2/tools/sgf/renderext',
                        location.origin
                    );

                    url.searchParams.set('sgf', `(${generatedSgf})`);
                    for (const [key, value] of Object.entries(params)) {
                        // Convert path parameter to lowercase for backend compatibility
                        const paramValue =
                            key === 'path' ? value.toLowerCase() : value;
                        url.searchParams.set(key, paramValue);
                    }

                    segments.push({
                        type: 'image',
                        url: url.toString(),
                        title: null,
                        alt: `Go diagram (${relativeType})`,
                    } as Image);
                } else {
                    // Fallback: keep original text if generation fails
                    segments.push({
                        type: 'text',
                        value: match[0],
                    } as Text);
                }

                lastIndex = fullEnd + 1;
                regex.lastIndex = fullEnd + 1;
            }

            if (lastIndex < node.value.length) {
                segments.push({
                    type: 'text',
                    value: node.value.slice(lastIndex),
                } as Text);
            }

            if (segments.length > 0) {
                (parent as Parent).children.splice(index, 1, ...segments);
            }
        });
    };
}
